import { webApi } from '@mi-front/api-clients';
import { useMemo } from 'react';
import { DEFAULT_QUALITY_MINIMAP_CONFIG } from '../consts';
import { VisibleTimeRange } from '../lib/visible-time-range';
import { MinimapConfig } from './minimap-config';
import {
  buildMinimapTimeBins,
  collectStationBadIntervalsMs,
  defaultStationHealthyInMinimapBin,
} from './quality-minimap-utils';

export type MinimapBin = {
  /** X position in pixels (brush inner coordinates). */
  x: number;
  /** Bar width in pixels (at least 1). */
  width: number;
  /** Fraction of stations healthy in this bin (0–1). */
  successRate: number;
};

export type UseMinimapBinsArgs = {
  report: webApi.StreamQualityReportResponseDto;
  fullDomain: VisibleTimeRange;
  innerWidth: number;
  minimapConfig?: MinimapConfig;
  /**
   * When set, only these stations are included in each bin’s success rate (same order as passed).
   * Defaults to `report.data.stationIds` when omitted.
   */
  stationIds?: string[];
};

/**
 * Computes fixed-width time bins over the report domain and, for each bin, the station-majority
 * success rate: average over stations of 1 if no issue/gap overlaps the bin, else 0.
 */
export function useMinimapBins({
  report,
  fullDomain,
  innerWidth,
  minimapConfig,
  stationIds: stationIdsProp,
}: UseMinimapBinsArgs): MinimapBin[] {
  const binCount = DEFAULT_QUALITY_MINIMAP_CONFIG.binCount;
  const isStationHealthyInBin =
    minimapConfig?.isStationHealthyInBin ?? defaultStationHealthyInMinimapBin;

  return useMemo(() => {
    const domainStartMs = fullDomain.from.getTime();
    const domainEndMs = fullDomain.to.getTime();
    if (!(domainEndMs > domainStartMs) || innerWidth <= 0 || binCount < 1) {
      return [];
    }

    const stationIds = stationIdsProp ?? report.data.stationIds;
    if (stationIds.length === 0) {
      return [];
    }

    const rowsById = new Map<string, webApi.StreamQualityPerStationDto>();
    for (const row of report.result.byStation) {
      rowsById.set(row.stationId, row);
    }

    const domainStart = fullDomain.from;
    const domainEnd = fullDomain.to;
    const badByStation = stationIds.map((id) =>
      collectStationBadIntervalsMs(rowsById.get(id), domainStart, domainEnd),
    );

    const timeBins = buildMinimapTimeBins(domainStartMs, domainEndMs, binCount);

    return timeBins.map((bin, index) => {
      let healthy = 0;
      for (let s = 0; s < badByStation.length; s += 1) {
        if (isStationHealthyInBin(badByStation[s], bin)) {
          healthy += 1;
        }
      }
      const successRate = healthy / stationIds.length;
      const x0 = (innerWidth * index) / binCount;
      const x1 = index === binCount - 1 ? innerWidth : (innerWidth * (index + 1)) / binCount;
      const x = x0;
      const width = Math.max(1, x1 - x0);
      return { x, width, successRate };
    });
  }, [
    report,
    fullDomain.from,
    fullDomain.to,
    innerWidth,
    binCount,
    isStationHealthyInBin,
    stationIdsProp,
  ]);
}
